import React, {
  useState,
  useRef,
  DragEvent,
  ClipboardEvent,
  ReactNode,
} from 'react';
import { FileUploadState } from './types';
import { useFileUpload } from './useFileUpload';
import './file-upload-dropzone.scss';

interface FileUploadDropZoneProps {
  children: ReactNode;
  onFileUpload: (fileState: FileUploadState) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Wraps children with drag-and-drop and paste file upload functionality.
 * Provides visual feedback during drag operations.
 */
export const FileUploadDropZone: React.FC<FileUploadDropZoneProps> = ({
  children,
  onFileUpload,
  disabled = false,
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const { processFile } = useFileUpload(onFileUpload);

  // Handle drag and drop
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        await processFile(files[i]);
      }
    }
  };

  // Handle paste events from children
  const handlePaste = async (e: ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Check if item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent pasting the image as text
        const file = item.getAsFile();
        if (file) {
          await processFile(file);
        }
      }
    }
  };

  return (
    <div
      className={`${className}${isDragging ? ' file-dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      {children}
      {isDragging && <div className='drag-overlay'></div>}
    </div>
  );
};
